import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Activity, CheckCircle2, AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { siteDisplay, fmtPct, fmtMs, fmtDate } from "../lib/monitor";

export default function PublicStatus() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/public/status/${slug}`);
      setData(res.data);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-[#0b1220]">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 dark:bg-[#0b1220] px-6 text-center">
        <Activity className="mb-3 text-slate-300" size={40} />
        <h1 className="font-heading text-xl font-bold text-slate-900">Status page not found</h1>
        <p className="mt-1 text-sm text-slate-500">This page may be private or the link is incorrect.</p>
      </div>
    );
  }

  const ok = data.overall === "operational";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0b1220]" data-testid="public-status-page">
      <header className="border-b border-slate-200 bg-white dark:bg-[#0f172a]">
        <div className="mx-auto flex max-w-3xl items-center gap-2.5 px-6 py-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
            <Activity size={18} className="text-white" strokeWidth={2.5} />
          </div>
          <span className="font-heading text-lg font-bold tracking-tight text-slate-900">{data.title}</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        {data.description && <p className="mb-6 text-sm text-slate-500">{data.description}</p>}

        {/* Overall banner */}
        <div
          className={`mb-8 flex items-center gap-3 rounded-lg border p-5 ${
            ok ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"
          }`}
          data-testid="status-overall-banner"
        >
          {ok ? <CheckCircle2 className="text-emerald-600" size={24} /> : <AlertTriangle className="text-rose-600" size={24} />}
          <div>
            <p className={`font-heading text-lg font-semibold ${ok ? "text-emerald-800" : "text-rose-800"}`}>
              {ok ? "All Systems Operational" : "Some Systems Are Down"}
            </p>
            <p className={`text-xs ${ok ? "text-emerald-600" : "text-rose-600"}`}>Updated {fmtDate(data.updated_at)}</p>
          </div>
        </div>

        {/* Services */}
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:bg-[#0f172a]">
          {data.services.length === 0 ? (
            <p className="p-8 text-center text-sm text-slate-400">No services are being displayed yet.</p>
          ) : (
            data.services.map((s, i) => {
              const d = siteDisplay(s);
              return (
                <div
                  key={i}
                  className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 last:border-0"
                  data-testid="status-service-row"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-900">{s.name}</p>
                    <p className="font-mono text-xs text-slate-400">
                      {fmtPct(s.uptime_30d)} uptime (30d){s.response != null ? ` · ${fmtMs(s.response)}` : ""}
                    </p>
                  </div>
                  <StatusBadge variant={d.variant} pulse={d.label === "UP"}>{d.label}</StatusBadge>
                </div>
              );
            })
          )}
        </div>

        <div className="mt-10 flex items-center justify-between text-xs text-slate-400">
          <button onClick={load} className="inline-flex items-center gap-1.5 hover:text-slate-600" data-testid="status-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
          <span>
            Product By{" "}
            <a href="https://www.omaxtech.com" target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:text-blue-700">Omaxtech Technologies</a>
          </span>
        </div>
      </main>
    </div>
  );
}
